import { useState } from 'react'
import { patchRequest } from '../../../shared/api/requests.api'
import type { Request } from '../../../shared/api/types'
import {
  isSellerEntity,
  isUserDateInputValid,
  SELLER_ENTITY_LABELS_RU,
  SellerEntity,
  USER_DATE_INPUT_MAX,
  USER_DATE_INPUT_MIN,
  USER_DATE_YEAR_RANGE_LABEL,
} from '@crm/shared'
import { ClientCombobox, type ClientOption } from './ClientCombobox'

export function EditRequestModal({
  request,
  onClose,
  onSaved,
}: {
  request: Request
  onClose: () => void
  onSaved: () => void
}) {
  // Клиент заявки целиком: поле показывает его, даже если его нет среди первых
  // клиентов выпадашки; другого клиента ищем на сервере (проверка Сергея 15.09.2026).
  const [client, setClient] = useState<ClientOption | null>(request.client ?? null)
  const clientId = client?.id ?? ''
  const [type, setType] = useState(request.type)
  // Юрлицо продажи. У заявок до 23.09 его нет — показываем СПЕЦМАШЭЛЕКТРО (так
  // и выставлялись КП и счёт) и отправляем, только если выбрали другое.
  const initialSellerEntity: SellerEntity = isSellerEntity(request.sellerEntityId)
    ? request.sellerEntityId
    : SellerEntity.SPM
  const [sellerEntityId, setSellerEntityId] = useState<SellerEntity>(initialSellerEntity)
  const [deadline, setDeadline] = useState(
    request.deadline ? new Date(request.deadline).toISOString().slice(0, 10) : '',
  )
  const [notes, setNotes] = useState(request.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      // Защита от абсурдного года из <input type="date"> (можно ввести 6 цифр).
      // Без неё toISOString() даёт "+222220-...Z" → Prisma падает 500.
      if (deadline && !isUserDateInputValid(deadline)) {
        setError(`Некорректный год дедлайна (допустимо ${USER_DATE_YEAR_RANGE_LABEL})`)
        setSaving(false)
        return
      }
      await patchRequest(request.id, {
        clientId,
        type,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        notes: notes.trim() || null,
        ...(sellerEntityId !== initialSellerEntity ? { sellerEntityId } : {}),
      })
      onSaved()
    } catch (e) {
      // Сервер отдаёт русский текст в error.message (422 — кривое значение, 403 —
      // чужой клиент). AxiosError.message — английское «Request failed with
      // status code 403», его сотруднику не показываем.
      const apiMsg = (e as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message
      setError(apiMsg ?? (e instanceof Error ? e.message : 'Ошибка сохранения'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Изменить заявку</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Клиент</label>
            <ClientCombobox clientId={clientId} selectedClient={client} onSelect={setClient} />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Тип</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as typeof type)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              <option value="TEKUCHKA">Текучка</option>
              <option value="PROJECT">Проект</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Юрлицо продажи</label>
            <select
              value={sellerEntityId}
              onChange={(e) => setSellerEntityId(e.target.value as SellerEntity)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              {Object.values(SellerEntity).map((id) => (
                <option key={id} value={id}>
                  {SELLER_ENTITY_LABELS_RU[id]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">
              От него по умолчанию выставляются КП и счёт. Уже выставленные не меняются.
            </p>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Дедлайн</label>
            <input
              type="date"
              value={deadline}
              min={USER_DATE_INPUT_MIN}
              max={USER_DATE_INPUT_MAX}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Заметка</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Необязательно..."
              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !clientId}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
